/**
 * LinkParentDialog — create a parent account + link it to a student (or
 * just link an existing parent account by email).
 *
 * Backed by:
 *   POST /api/admin/parents/create { name, email, password?, student_ids[] }
 *
 * Visible to super_admin, hostel_admin, and school_admin from the student
 * row "Link parent" button.
 */
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UserPlus, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export function LinkParentButton({ student, onLinked }) {
  const [open, setOpen] = useState(false);
  const isSingle = !!student?.is_single_parent;
  const singleType = student?.single_parent_type || "";
  const [form, setForm] = useState({ name: "", email: "" });
  const [whichParent, setWhichParent] = useState(singleType || "father");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const computeName = React.useCallback((which) => {
    if (which === "father") return student?.parent_father_name || student?.guardian_name || "";
    if (which === "mother") return student?.parent_mother_name || student?.guardian_name || "";
    return student?.parent_father_name || student?.parent_mother_name || student?.guardian_name || "";
  }, [student]);

  // Pre-fill from the student's parent fields. Single-parent students lock
  // the toggle to the active guardian; dual-parent students default to Father
  // but admins can flip the toggle to switch to Mother.
  React.useEffect(() => {
    if (open) {
      const which = singleType || "father";
      setWhichParent(which);
      setForm({
        name: computeName(which),
        email: student?.parent_email || "",
      });
      setCreated(null);
    }
  }, [open, student, singleType, computeName]);

  const onParentSwitch = (which) => {
    setWhichParent(which);
    setForm((f) => ({ ...f, name: computeName(which) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Parent name + email are required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        parent_name: form.name,
        parent_email: form.email.trim().toLowerCase(),
        student_ids: [student.id],
      };
      const res = await api.post("/admin/parents/create", body);
      // Normalise the backend's field name (`temp_password`) into the form
      // the UI expects (`temporary_password`) and surface the parent email.
      setCreated({
        email: body.parent_email,
        temporary_password: res.data.temp_password,
        students_linked: res.data.students_linked,
      });
      toast.success(`Parent ${form.email} linked to ${student.name}`);
      onLinked?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyPw = () => {
    if (!created?.temporary_password) return;
    navigator.clipboard?.writeText(created.temporary_password);
    toast.success("Temporary password copied");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`link-parent-${student.id}`}>
          <UserPlus size={12} className="mr-1.5" /> Link parent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link parent to {student.name}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-3" data-testid="link-parent-result">
            <p className="text-sm">
              Parent account <strong>{created.email}</strong> is now linked to {student.name}. They can sign in to the parent portal to view marks, fees, progress, and pay.
            </p>
            {created.temporary_password && (
              <div className="vs-card p-3 bg-[var(--vs-bg-subtle)]">
                <Label className="text-xs">Temporary password (share securely)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 px-2 py-1.5 bg-white border border-[var(--vs-border)] rounded font-mono text-sm" data-testid="link-parent-temp-pw">
                    {created.temporary_password}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyPw} data-testid="link-parent-copy">
                    <Copy size={12} />
                  </Button>
                </div>
                <p className="text-[11px] text-[var(--vs-text-secondary)] mt-1">
                  Parent will be asked to change this on first sign-in.
                </p>
              </div>
            )}
            <Button onClick={() => setOpen(false)} className="w-full" data-testid="link-parent-close">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-[var(--vs-text-secondary)]">
              Creates a parent account (if one doesn't exist for that email) and links it to <strong>{student.name}</strong>. If the email already belongs to a parent, we just add the link.
            </p>
            {isSingle ? (
              <div className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-md bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)]/40" data-testid="lp-single-parent-tag">
                <span className="inline-block px-1.5 py-0.5 rounded bg-[var(--vs-secondary)]/10 text-[var(--vs-secondary)] font-mono uppercase text-[10px]">Single parent</span>
                Linking the <strong className="capitalize">{singleType || "guardian"}</strong> — the other parent field is not applicable for this student.
              </div>
            ) : (
              <div>
                <Label className="text-xs">Which parent?</Label>
                <div className="flex gap-2 mt-1" data-testid="lp-which-parent">
                  {["father", "mother"].map((p) => (
                    <button
                      type="button"
                      key={p}
                      onClick={() => onParentSwitch(p)}
                      data-testid={`lp-which-${p}`}
                      className={`flex-1 capitalize px-3 py-1.5 rounded-md text-xs border transition ${
                        whichParent === p
                          ? "bg-[var(--vs-secondary)] text-[var(--vs-bg)] border-[var(--vs-secondary)]"
                          : "bg-white border-[var(--vs-border)] hover:border-[var(--vs-secondary)]/40"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="lp-name">Parent name *</Label>
              <Input id="lp-name" data-testid="lp-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" />
            </div>
            <div>
              <Label htmlFor="lp-email">Parent email *</Label>
              <Input id="lp-email" data-testid="lp-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white" placeholder="parent@example.org" />
              <p className="text-[11px] text-[var(--vs-text-secondary)] mt-1">
                A temporary password is auto-generated and emailed if delivery is configured. You'll also see it on the next screen for secure handoff.
              </p>
            </div>
            <Button type="submit" disabled={busy} data-testid="lp-submit" className="w-full bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
              <UserPlus size={12} className="mr-1.5" /> {busy ? "Linking…" : "Create + link parent"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default LinkParentButton;
